import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { EyeCatcher, URLs } from '../../shared/models/eye-catcher.model';
import { EyeCatcherService } from './../../shared/services/eye-catcher.service';

@Component({
  selector: 'app-eye-catcher-page',
  imports: [
    CommonModule,
    CardModule,
    RouterModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    ToastModule,
    TableModule,
    ButtonModule,
    DatePickerModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    TooltipModule,
    SelectModule,
    MultiSelectModule,
    FloatLabelModule,
    DialogModule,
    FileUploadModule,
    DatePipe,
    TagModule,
    ConfirmDialogModule,
  ],
  providers: [MessageService, EyeCatcherService, ConfirmationService],
  templateUrl: './eye-catcher-page.component.html',
  styleUrl: './eye-catcher-page.component.scss',
})
export class EyeCatcherPageComponent implements OnInit {
  @ViewChild('image') fileUploadComponent!: FileUpload;
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);
  #translateService = inject(TranslateService);
  eyeCatcherService = inject(EyeCatcherService);

  eyeCatcherPopupVisible = false;
  eyeCatcherForm: FormGroup;
  currentEyeCatcher: EyeCatcher | null = null;

  rangeDates: Date[] = [
    (() => {
      const date = new Date();
      date.setDate(date.getDate() - 7);
      date.setHours(0, 0, 0, 0);
      return date;
    })(),
  ];

  urlOptions = URLs.sort((a, b) => a.value.localeCompare(b.value));

  selectedUrl = this.urlOptions.filter((option) => option.key === '')[0];

  eyeCatchers: EyeCatcher[] = [
    {
      id: 1,
      image: '',
      url: '/impressions',
      valid_from: new Date().toISOString(),
      valid_until: new Date().toISOString(),
    },
  ];

  constructor(private fb: FormBuilder) {
    this.eyeCatcherForm = this.fb.group({
      url: ['', [Validators.required]],
      valid_dates: [null, [Validators.required, this.#validDateRange]],
    });
  }

  #validDateRange(control: import('@angular/forms').AbstractControl) {
    const value = control.value;
    if (!Array.isArray(value) || !value[0] || !value[1]) {
      return { validDateRange: true };
    }
    return null;
  }

  ngOnInit(): void {
    this.eyeCatcherService.getEyeCatchers();
  }

  selectDateRange() {
    if (this.rangeDates[0] && this.rangeDates[1]) {
      const time_from = new Date(this.rangeDates[0]);
      time_from.setHours(0, 0, 0, 0);

      const time_to = new Date(this.rangeDates[1]);
      time_to.setHours(23, 59, 59, 999);
    }
  }

  onRowEditInit(eyeCatcher: EyeCatcher) {
    this.eyeCatcherForm.patchValue({
      url: eyeCatcher.url,
      valid_dates: [
        eyeCatcher.valid_from ? new Date(eyeCatcher.valid_from) : null,
        eyeCatcher.valid_until ? new Date(eyeCatcher.valid_until) : null,
      ],
    });
    this.currentEyeCatcher = eyeCatcher;
    this.eyeCatcherPopupVisible = true;
  }

  onRowDeleteInit(event: Event, eyeCatcher: EyeCatcher) {
    this.#confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.#translateService.instant(
        'eyeCatcher.deleteConfirmMessage',
      ),
      header: this.#translateService.instant('eyeCatcher.deleteHeader'),
      rejectLabel: this.#translateService.instant('label.cancel'),
      rejectButtonProps: {
        label: this.#translateService.instant('label.cancel'),
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: this.#translateService.instant('label.delete'),
        severity: 'danger',
      },

      accept: () => {
        this.eyeCatcherService.deleteEyeCatcher(eyeCatcher.id).subscribe({
          next: () => {
            this.eyeCatcherService.getEyeCatchers();
            this.#messageService.add({
              severity: 'success',
              summary: this.#translateService.instant(
                'label.successfullyDeletedHeader',
              ),
              detail: this.#translateService.instant(
                'label.successfullyDeleted',
              ),
            });
          },
          error: (e: any) => {
            this.#messageService.add({
              severity: 'error',
              summary: this.#translateService.instant('label.errorHeader'),
              detail: this.#translateService.instant('label.errorDeleting'),
            });
          },
        });
      },
      reject: () => {
        //nothing
      },
    });
  }

  showEyeCatcherPopup() {
    this.eyeCatcherPopupVisible = true;
    this.currentEyeCatcher = null;
  }

  onSubmit() {
    if (!this.eyeCatcherForm.valid) {
      this.eyeCatcherForm.markAllAsTouched();
      return;
    }
    const formData = this.eyeCatcherForm.value;
    const newFile = this.fileUploadComponent.files[0];
    if (this.currentEyeCatcher && this.currentEyeCatcher.id > 0) {
      this.eyeCatcherService
        .updateEyeCatcher(formData, newFile, this.currentEyeCatcher.id)
        .subscribe({
          next: (eyeCatcher: any) => {
            this.eyeCatcherService.getEyeCatchers();
            this.closeEyeCatcherPopup();
          },
          error: (e: any) => console.log(e),
        });
    } else {
      if (!newFile) {
        this.#messageService.add({
          severity: 'error',
          summary: 'Fehler',
          detail: 'Bitte ein Bild hochladen.',
        });
        return;
      }
      this.eyeCatcherService.createEyeCatcher(formData, newFile).subscribe({
        next: (eyeCatcher: any) => {
          this.eyeCatcherService.getEyeCatchers();
          this.closeEyeCatcherPopup();
        },
        error: (e: any) => console.log(e),
      });
    }
  }

  closeEyeCatcherPopup() {
    this.eyeCatcherPopupVisible = false;
    this.fileUploadComponent.clear();
    this.eyeCatcherForm.reset();
    this.currentEyeCatcher = null;
  }

  onUpload(e: any) {}

  getLinkName(url: string): string {
    return URLs.find((option) => option.key === url)?.value || url;
  }
}
